import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';

import { getSupplier, getSupplierFromUserId, selectSupplier, selectSupplierState } from '../../store/entities/supplier';
import { supplierEmployeeListActions } from '../../store/entities/supplierEmployeeList';
import { QcsMenuItem } from '../common/basic/QcsMenuItem';
import { DetailHeader } from '../common/DetailHeader';
import { FormContainer } from '../common/form/FormContainer';
import { Input } from '../common/form/Input';
import { Select } from '../common/form/Select';
import { SubmitButton } from '../common/form/SubmitButton';
import { SupplierEmployeeRoleSelect } from '../common/form/SupplierEmployeeRoleSelect';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import {
  getSupplierEmployee,
  selectSupplierEmployee,
  selectSupplierEmployeeState,
  supplierEmployeeActions,
} from '../../store/entities/supplierEmployee';
import { QcsTextField } from '../common/basic/QcsTextField';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { SUPPORTED_LANGUAGES } from '../../utils/i18n';
import { SupplierEmployeeDocument } from './SupplierEmployeeDocument';
import { PhoneInput } from '../common/form/PhoneInput';
import { hasRole } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';
import { QcsBox } from '../common/basic/QcsBox';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsTypography } from '../common/basic/QcsTypography';
import { Box, Link } from '@mui/material';
import QcsAlert from '../common/basic/QcsAlert';
import { Link as RouterLink } from 'react-router-dom';
import { PathKey, useNavigation } from '../../utils/urlPaths';
import { formatChangeInfo, getFirstLastNameObj } from '../../utils/format';
import { SupplierEmployeeDto } from '@api/models/suppliers/SupplierEmployeeDto';
import { QcsToggleButtonFields } from '../common/form/QcsToggleButtonFields';
import { applicationInvitationService, supplierEmployeeService } from '@api/services/services';
import { resources } from '@api/resources';
import { UserRole } from '@api/models/enums/UserRole';
import { SUPPLIER_EMPLOYEE_STATE_TRANSITIONS, UserState } from '@api/models/enums/UserState';
import { ShowError } from '../ShowError';
import { ShowResourceContent } from '../ShowResourceContent';
import { SupplierEmployeePinReveal } from '../common/SupplierEmployeePinReveal';
import { ShowImageButton } from '../invitation/Detail/Tabs/ShowImageButton';
import { SubmitButtonsContainer } from '../common/form/SubmitButtonsContainer';

interface SupplierEmployeeDetailProps {
  createNew?: boolean;
  forSupplierId?: string;
  forceRole?: UserRole;
  onSaved?: (createdEmployee?: SupplierEmployeeDto) => void;
}

export const SupplierEmployeeDetail: FC<SupplierEmployeeDetailProps> = ({ createNew, forSupplierId, forceRole, onSaved } = {}) => {
  const { t } = useTranslation();
  const params = useParams();
  const supplierId = forSupplierId ?? params.supplierId;
  const supplierEmployeeId = createNew ? 'new' : params.supplierEmployeeId;
  const identity = useAppSelector(selectIdentity);
  const { goBack, getPath, navigate } = useNavigation();
  const supplier = useAppSelector(selectSupplier);
  const supplierEmployee = useAppSelector(selectSupplierEmployee);
  const companyCustomization = useAppSelector(selectCompanyCustomization);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (supplierEmployeeId === 'new') {
      dispatch(supplierEmployeeActions.default());
    } else {
      dispatch(getSupplierEmployee(supplierEmployeeId!));
    }

    if (hasRole(identity.roles, [UserRole.ExternalManager])) {
      dispatch(getSupplierFromUserId(identity.id));
    } else {
      dispatch(getSupplier(supplierId!));
    }
  }, [supplierEmployeeId, supplierId, dispatch, identity.roles, identity.id]);

  const handleSubmit = async (data: SupplierEmployeeDto) => {
    if (data.role === UserRole.ExternalManager && data.email === '') {
      data.email = undefined;
    }
    let createdEmployee: SupplierEmployeeDto | undefined;
    if (supplierEmployeeId === 'new') {
      const response = await supplierEmployeeService.create(data, { supplierId: supplier.id! });
      createdEmployee = { ...data, id: response.data.id };
    } else {
      await supplierEmployeeService.update(supplierEmployeeId!, data);
    }

    dispatch(supplierEmployeeListActions.reload(true));
    if (onSaved) {
      onSaved(createdEmployee);
    } else {
      goBack();
    }
  };

  const handleResendInvitation = async () => {
    await applicationInvitationService.resendApplicationInvitation(supplierEmployee.id!);
  };

  // The employee belongs to a different supplier than the one in the URL — point at the right
  // one instead of showing the form.
  const employeeSupplier = supplierEmployee.supplier;
  const isWrongSupplier = supplierEmployeeId !== 'new' && !!supplierId && !!employeeSupplier?.id && employeeSupplier.id !== supplierId;

  return (
    <ShowResourceContent
      fetchStateSelector={[selectSupplierEmployeeState, selectSupplierState]}
      resource={[resources.getSupplierFromUserId, resources.getSupplier, resources.getSupplierEmployee]}
    >
      {!onSaved && <DetailHeader title="supplierEmployee.detailTitle" showBackTo />}
      {isWrongSupplier ? (
        <QcsAlert severity="warning" sx={{ mt: 2, p: 2 }}>
          <QcsTypography>
            {t('supplierEmployee.wrongSupplier.notFound')}{' '}
            <Trans
              i18nKey="supplierEmployee.wrongSupplier.hint"
              values={{
                employeeName: getFirstLastNameObj(supplierEmployee),
                supplierName: employeeSupplier.name,
              }}
              components={{
                employeeLink: (
                  <Link
                    component={RouterLink}
                    to={getPath(PathKey.SupplierEmployee, { supplierId: employeeSupplier.id, employeeId: supplierEmployee.id! })}
                  />
                ),
              }}
            />
          </QcsTypography>
          <QcsBox sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <QcsButton variant="contained" color="warning" onClick={() => navigate(PathKey.SupplierEmployees, { supplierId })}>
              {t('supplierEmployee.wrongSupplier.showEmployees')}
            </QcsButton>
          </QcsBox>
        </QcsAlert>
      ) : (
        <>
          <FormikDebounce<SupplierEmployeeDto>
            initialValues={{
              ...supplierEmployee,
              ...(forceRole ? { role: forceRole } : {}),
              language: supplierEmployeeId === 'new' ? (companyCustomization?.defaultLanguage ?? '') : supplierEmployee.language,
            }}
            validateOnChange={true}
            validationSchema={Yup.object().shape({
              name: validations.stringRequired(t),
              lastName: validations.stringRequired(t),
              phone: validations.phoneOptional(t),
              email: Yup.string().when('role', ([role]) =>
                role === UserRole.ExternalManager ? validations.emailRequired(t) : validations.emailOptional(t),
              ),
              language: validations.stringRequired(t),
            })}
            onSubmit={handleSubmit}
          >
            {({ values }) => (
              <Form>
                <FormContainer>
                  <QcsTextField label={t('supplierEmployee.supplier')} value={supplier.name} disabled />
                  <Input name="name" label={t('supplierEmployee.name')} maxLength={100} required />
                  <Input name="lastName" label={t('supplierEmployee.lastName')} maxLength={100} required />
                  <Input name="jobPosition" label={t('supplierEmployee.jobPosition')} maxLength={100} />
                  {!forceRole && <SupplierEmployeeRoleSelect name="role" label={t('supplierEmployee.role')} />}
                  <PhoneInput name="phone" label={t('supplierEmployee.phone')} />
                  <Input
                    name="email"
                    removeSpaces
                    label={t('supplierEmployee.email')}
                    maxLength={100}
                    required={values.role === UserRole.ExternalManager}
                  />
                  <LanguageSelect
                    name="language"
                    label={t('user.language')}
                    languages={SUPPORTED_LANGUAGES}
                    allowedLanguages={companyCustomization?.applicationLanguages}
                    required
                  />
                  {supplierEmployeeId !== 'new' && (
                    <QcsBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto max-content',
                        gap: '1rem',
                      }}
                    >
                      <Select name="state" renderValue={(value) => t(`entityState.${value}`)} label={t('supplierEmployee.state')}>
                        {[supplierEmployee.state, ...SUPPLIER_EMPLOYEE_STATE_TRANSITIONS[supplierEmployee.state]].map((state) => (
                          <QcsMenuItem key={state} value={state}>
                            {t(`entityState.${state}`)}
                          </QcsMenuItem>
                        ))}
                      </Select>
                      <QcsLoadingButton
                        resourceLoading={resources.resendApplicationInvitation}
                        onClick={handleResendInvitation}
                        disabled={supplierEmployee.state !== UserState.Invited}
                      >
                        {t('supplierEmployee.sendInvitation')}
                      </QcsLoadingButton>
                    </QcsBox>
                  )}
                  <Box>
                    <QcsToggleButtonFields
                      name="carAccessAllowed"
                      disabled={!hasRole(identity.roles, [UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.AdminCompany, UserRole.AdminQcs])}
                      options={[
                        { value: true, label: t('supplier.car.allowEntry') },
                        { value: false, label: t('supplier.car.denyEntry') },
                      ]}
                    />
                    {supplierEmployee.carAccessAllowedChanged?.at && (
                      <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: -1.5 }}>
                        {t('common.lastChange')}: {formatChangeInfo(supplierEmployee.carAccessAllowedChanged)}
                      </Box>
                    )}
                  </Box>

                  <Input name="note" label={t('supplierEmployee.note')} maxLength={250} multiline={true} rows={4} />

                  {supplierEmployeeId !== 'new' &&
                    hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]) && (
                      <SupplierEmployeePinReveal supplierEmployeeId={supplierEmployeeId!} />
                    )}

                  <ShowError resource={[resources.createSupplierEmployee, resources.updateSupplierEmployee]} />
                  <QcsBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    {supplierEmployeeId !== 'new' &&
                      hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]) && (
                        <ShowImageButton
                          documentUrl=""
                          variant="outlined"
                          silentError
                          fetchDocument={async () => {
                            const res = await supplierEmployeeService.getSupplierEmployeeProtocol(supplierEmployeeId!, { responseType: 'blob' });
                            return {
                              data: res.data as Blob,
                              headers: { 'content-type': 'application/pdf' },
                            };
                          }}
                        >
                          {t('supplierEmployee.showProtocol')}
                        </ShowImageButton>
                      )}
                    <SubmitButtonsContainer>
                      <SubmitButton />
                    </SubmitButtonsContainer>
                  </QcsBox>
                </FormContainer>
              </Form>
            )}
          </FormikDebounce>

          {supplierEmployeeId !== 'new' &&
            hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]) && (
              <SupplierEmployeeDocument />
            )}
        </>
      )}
    </ShowResourceContent>
  );
};
